// components/MonthlyDigestPage.js
// Dedicated page for the redesigned Monthly Digest — patient picker + a
// month/year picker + a report-type picker, rendered with the same
// dial/stats layout as everywhere else in the app. Stats are always live
// (they don't change month to month); only the AI update text changes per
// selection. No PDF — this is a live page, print via the browser if wanted.
import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  Box, Typography, Autocomplete, TextField, Select, MenuItem, CircularProgress, Alert, Button,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import { motion } from "framer-motion";
import axios from "axios";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import CalendarMonthRoundedIcon from "@mui/icons-material/CalendarMonthRounded";
import UserContext from "./UserContext";
import ThemeToggle from "./ThemeToggle";
import ScoreDial from "./reports/ScoreDial";
import { levelColor, levelBg } from "./reports/reportHelpers";

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:8000";
const BRAND = { navy: "#0a0e27", navyMid: "#1a2244", accent: "#637cdb" };

const REPORT_TYPE_LABELS = { opioid_risk: "Opioid Risk", pain_perception: "Pain Perception", pgx: "Drug Metabolism" };
const STAT_LABELS = {
  proove_ori: "Proove Opioid Risk", ort: "Opioid Risk Tool (ORT)", phq2: "Personal Health Questionnaire (PHQ-2)",
  self_reported_pain: "Self-Reported Pain", pss: "Perceived Stress Scale (PSS)", comt_predisposition: "Genetic Predisposition (COMT)",
};

function monthLabel(ym) {
  const [y, m] = ym.split("-");
  return new Date(Number(y), Number(m) - 1, 1).toLocaleDateString(undefined, { month: "long", year: "numeric" });
}
function patientOptionLabel(p) {
  const name = `${p.first_name || ""} ${p.last_name || ""}`.trim() || "Patient";
  return p.date_of_birth ? `${name} (${p.date_of_birth})` : name;
}

export default function MonthlyDigestPage() {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const navigate = useNavigate();
  const { user } = useContext(UserContext);
  const token = user?.token;
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const [patients, setPatients] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [loadingPatients, setLoadingPatients] = useState(true);

  const [digest, setDigest] = useState(null);
  const [loadingDigest, setLoadingDigest] = useState(false);
  const [reportType, setReportType] = useState("");
  const [yearMonth, setYearMonth] = useState("");
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!token) return;
    let mounted = true;
    axios.get(`${API_BASE}/me/patients`, { headers })
      .then((res) => { if (mounted) { const list = res.data?.patients || []; setPatients(list); setSelectedPatient(list[0] || null); } })
      .catch(() => { if (mounted) setError("Couldn't load your patient list."); })
      .finally(() => { if (mounted) setLoadingPatients(false); });
    return () => { mounted = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    if (!token || !selectedPatient) return;
    let mounted = true;
    setLoadingDigest(true);
    setError(null);
    axios.get(`${API_BASE}/me/monthly-digest`, {
      headers,
      params: { name_hash: selectedPatient.name_hash || "", dob_hash: selectedPatient.dob_hash || "" },
    })
      .then((res) => {
        if (!mounted) return;
        setDigest(res.data);
        const firstType = res.data.report_types?.[0] || "";
        setReportType(firstType);
        const months = Object.keys(res.data.updates?.[firstType] || {}).sort().reverse();
        setYearMonth(months[0] || "");
      })
      .catch(() => { if (mounted) setError("Couldn't load the monthly digest for this patient."); })
      .finally(() => { if (mounted) setLoadingDigest(false); });
    return () => { mounted = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, selectedPatient?.name_hash, selectedPatient?.dob_hash]);

  const availableMonths = useMemo(
    () => Object.keys(digest?.updates?.[reportType] || {}).sort().reverse(),
    [digest, reportType]
  );
  useEffect(() => {
    if (availableMonths.length && !availableMonths.includes(yearMonth)) setYearMonth(availableMonths[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportType, availableMonths.join(",")]);

  const currentUpdate = digest?.updates?.[reportType]?.[yearMonth];
  const border = isDark ? "rgba(255,255,255,0.08)" : "rgba(10,14,39,0.08)";
  const cardBg = isDark ? "rgba(17,24,39,0.62)" : "rgba(255,255,255,0.72)";
  const textSub = isDark ? "#94a3b8" : "#64748b";

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: isDark ? "#0a0f1e" : "#f0f4ff", position: "relative" }}>
      <Box sx={{ position: "relative", zIndex: 10, maxWidth: 1000, mx: "auto", px: { xs: 2, md: 4 }, py: { xs: 3, md: 5 } }}>
        <motion.div initial={{ y: -16, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.45 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 4, flexWrap: "wrap", gap: 2 }}>
            <Box>
              <Button onClick={() => navigate("/home")} startIcon={<ArrowBackRoundedIcon sx={{ fontSize: 16 }} />}
                sx={{ textTransform: "none", fontSize: 12.5, fontWeight: 600, mb: 1.5, px: 0, color: textSub, "&:hover": { bgcolor: "transparent", color: BRAND.accent } }}>
                Back
              </Button>
              <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.14em", color: BRAND.accent, mb: 0.75 }}>MONTHLY UPDATES</Typography>
              <Typography sx={{ fontSize: 30, fontWeight: 800, color: isDark ? "#f1f5f9" : BRAND.navy, lineHeight: 1.2 }}>Monthly Digest</Typography>
              <Typography sx={{ fontSize: 13.5, color: textSub, mt: 0.75 }}>Genuinely new findings relevant to your results, verified and sourced.</Typography>
            </Box>
            <ThemeToggle />
          </Box>
        </motion.div>

        {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{error}</Alert>}

        <motion.div initial={{ y: 16, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.5, delay: 0.1 }}>
          <Box sx={{
            borderRadius: "22px", overflow: "hidden", p: { xs: 2, sm: 3 },
            bgcolor: cardBg, backdropFilter: "blur(20px) saturate(140%)", WebkitBackdropFilter: "blur(20px) saturate(140%)",
            border: `1px solid ${border}`, boxShadow: isDark ? "0 24px 80px rgba(0,0,0,0.4)" : "0 24px 80px rgba(10,14,39,0.08)",
          }}>
            {/* Pickers */}
            <Box sx={{
              display: "flex", flexWrap: "wrap", gap: 2, alignItems: "center", p: { xs: 2, sm: 2.5 }, mb: 3,
              borderRadius: "16px", background: `linear-gradient(135deg, ${BRAND.navy} 0%, ${BRAND.navyMid} 100%)`,
            }}>
              <CalendarMonthRoundedIcon sx={{ color: "#a8b6f0", fontSize: 22 }} />
              {loadingPatients ? <CircularProgress size={18} sx={{ color: "#a8b6f0" }} /> : (
                <Autocomplete
                  size="small" sx={{ minWidth: 220, flex: "0 1 300px" }}
                  options={patients} value={selectedPatient} disableClearable
                  onChange={(_, v) => setSelectedPatient(v)}
                  getOptionLabel={patientOptionLabel}
                  isOptionEqualToValue={(a, b) => a.name_hash === b.name_hash && a.dob_hash === b.dob_hash}
                  renderInput={(params) => (
                    <TextField {...params} variant="filled" InputProps={{ ...params.InputProps, disableUnderline: true,
                      sx: { bgcolor: "rgba(255,255,255,0.08)", borderRadius: "10px", color: "#fff", fontSize: 13.5, "& input": { color: "#fff" } } }}
                      sx={{ "& .MuiFilledInput-input": { py: "9px !important" } }} />
                  )}
                />
              )}
              <Select size="small" value={reportType} onChange={(e) => setReportType(e.target.value)}
                disabled={!digest?.report_types?.length}
                sx={{ minWidth: 170, bgcolor: "rgba(255,255,255,0.08)", borderRadius: "10px", color: "#fff", fontSize: 13.5,
                  "& .MuiSelect-icon": { color: "#a8b6f0" }, "& fieldset": { border: "none" } }}>
                {(digest?.report_types || []).map((rt) => (
                  <MenuItem key={rt} value={rt}>{REPORT_TYPE_LABELS[rt] || rt}</MenuItem>
                ))}
              </Select>
              <Select size="small" value={yearMonth} onChange={(e) => setYearMonth(e.target.value)}
                disabled={!availableMonths.length}
                sx={{ minWidth: 160, bgcolor: "rgba(255,255,255,0.08)", borderRadius: "10px", color: "#fff", fontSize: 13.5,
                  "& .MuiSelect-icon": { color: "#a8b6f0" }, "& fieldset": { border: "none" } }}>
                {availableMonths.map((ym) => (
                  <MenuItem key={ym} value={ym}>{monthLabel(ym)}</MenuItem>
                ))}
              </Select>
            </Box>

            {loadingDigest ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}><CircularProgress size={22} sx={{ color: BRAND.accent }} /></Box>
            ) : !digest?.report_types?.length ? (
              <Typography sx={{ fontSize: 13.5, color: textSub, textAlign: "center", py: 3 }}>
                No reports yet for {selectedPatient?.first_name || "this patient"}.
              </Typography>
            ) : (
              <>
                {/* Demographics + stats, same visual language as Summary View */}
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, mb: 3 }}>
                  {Object.entries(digest.stats?.[reportType] || {}).map(([key, stat]) => (
                    stat && (stat.level != null || stat.score != null) && (
                      <Box key={key} sx={{ flex: "1 1 150px", minWidth: 140, maxWidth: 180, textAlign: "center", borderRadius: "14px", p: 1.75,
                        bgcolor: isDark ? "rgba(255,255,255,0.03)" : "#fff", border: `1px solid ${border}` }}>
                        <Typography sx={{ fontSize: 10, fontWeight: 700, color: textSub, mb: 0.75, textTransform: "uppercase" }}>
                          {STAT_LABELS[key] || key}
                        </Typography>
                        <Box sx={{ display: "flex", justifyContent: "center", transform: "scale(0.82)" }}>
                          <ScoreDial level={stat.level} score={stat.score} />
                        </Box>
                        <Box sx={{ mt: 0.5, display: "inline-block", borderRadius: 999, px: 1.25, py: 0.3, fontSize: 10.5, fontWeight: 700,
                          color: levelColor(stat.level), bgcolor: levelBg(stat.level) }}>
                          {stat.level || "N/A"}
                        </Box>
                      </Box>
                    )
                  ))}
                </Box>

                {/* This month's AI update */}
                {currentUpdate ? (
                  <Box sx={{ borderRadius: "16px", border: `1px solid ${border}`, overflow: "hidden" }}>
                    <Box sx={{ bgcolor: "#575555", color: "#fff", px: 2.5, py: 1.5, fontSize: 14, fontWeight: 700 }}>
                      {monthLabel(yearMonth)} Update — {REPORT_TYPE_LABELS[reportType] || reportType}
                    </Box>
                    <Box sx={{ p: 2.5, fontSize: 13.5, lineHeight: 1.8, color: isDark ? "#e2e8f0" : "#1e293b" }}
                      dangerouslySetInnerHTML={{ __html: currentUpdate.summary_html }} />
                    {!!(currentUpdate.sources || []).length && (
                      <Box sx={{ px: 2.5, pb: 2.5, display: "flex", flexWrap: "wrap", gap: 1 }}>
                        {currentUpdate.sources.map((s, i) => (
                          <Box key={i} component="a" href={s.url} target="_blank" rel="noopener noreferrer"
                            sx={{ fontSize: 11, color: BRAND.accent, textDecoration: "none", "&:hover": { textDecoration: "underline" } }}>
                            {s.title || s.url}
                          </Box>
                        ))}
                      </Box>
                    )}
                  </Box>
                ) : (
                  <Typography sx={{ fontSize: 13.5, color: textSub, textAlign: "center", py: 3 }}>
                    No monthly update available for this selection yet.
                  </Typography>
                )}
              </>
            )}
          </Box>
        </motion.div>
      </Box>
    </Box>
  );
}
